// An editable spreadsheet grid, for planning. Works like Excel:
//  - click, drag, Shift+click or Shift+arrows to select; arrows, Tab and Enter to move
//  - type to replace a cell, F2 or double-click to edit it, Enter or Tab to commit, Esc to cancel
//  - entries: 12500, 12.5k, 1.2m, (500) for negative, +5% or -3% to change the current value, =1200*12/4
//  - Delete clears the selection to 0; Ctrl/Cmd+C copies; Ctrl/Cmd+V pastes a block straight from Excel
//  - Ctrl/Cmd+R fills the first column of the selection to the right; Ctrl/Cmd+D fills the top row down
//  - Ctrl/Cmd+Z undoes (the page keeps the history)
// Cells that can't be edited (totals, driven lines, a locked version) stay selectable and copyable.
import { useEffect, useRef, useState, type ReactNode } from "react";

export type SCol = { key: string; label: string; sub?: string; num: boolean; width: number; pinned?: boolean };
export type SCell = { v: number | string | null; text: string; edit?: boolean; tone?: "bad" | "good" | "muted" | "driven" | "warn"; cls?: string; title?: string };
export type SRow = { key: string; kind: "head" | "line" | "sub" | "total"; cells: SCell[]; cls?: string };
export type SEdit = { row: number; col: number; value: number };
export type SStats = { count: number; nums: number; sum: number };

/** Read what someone typed into a number; null when it isn't one. */
export function parseEntry(text: string, current: number): number | null {
  let t = text.trim().replace(/[$,\s]/g, "");
  if (!t) return 0;
  const pct = /^([+-])(\d+(?:\.\d+)?)%$/.exec(t);
  if (pct) return round2(current * (1 + (pct[1] === "-" ? -1 : 1) * Number(pct[2]) / 100));
  if (t.startsWith("=")) return evaluate(t.slice(1));
  const neg = /^\((.*)\)$/.exec(t); if (neg) t = "-" + neg[1];
  const m = /^(-?\d*\.?\d+)([kKmM]?)$/.exec(t);
  if (!m) return null;
  return round2(Number(m[1]) * (m[2].toLowerCase() === "k" ? 1e3 : m[2].toLowerCase() === "m" ? 1e6 : 1));
}
const round2 = (x: number) => Math.round(x * 100) / 100;
/** + - * / ( ) and % on numbers only, no names: safe to evaluate what's typed. */
function evaluate(src: string): number | null {
  const toks = src.replace(/,/g, "").match(/\d*\.?\d+(?:[eE][+-]?\d+)?[kKmM%]?|[-+*/()]/g);
  if (!toks || toks.join("") !== src.replace(/[\s,]/g, "")) return null;
  let i = 0;
  const numTok = (t: string) => { const m = /^(.*?)([kKmM%]?)$/.exec(t)!; const n = Number(m[1]); return m[2] === "%" ? n / 100 : m[2].toLowerCase() === "k" ? n * 1e3 : m[2].toLowerCase() === "m" ? n * 1e6 : n; };
  const expr = (): number => { let a = term(); while (toks[i] === "+" || toks[i] === "-") { const op = toks[i++]; const b = term(); a = op === "+" ? a + b : a - b; } return a; };
  const term = (): number => { let a = factor(); while (toks[i] === "*" || toks[i] === "/") { const op = toks[i++]; const b = factor(); a = op === "*" ? a * b : a / b; } return a; };
  const factor = (): number => {
    const t = toks[i++];
    if (t === "-") return -factor();
    if (t === "+") return factor();
    if (t === "(") { const v = expr(); if (toks[i++] !== ")") throw new Error(")"); return v; }
    if (t === undefined || /[-+*/()]/.test(t)) throw new Error("bad");
    return numTok(t);
  };
  try { const v = expr(); return i === toks.length && Number.isFinite(v) ? round2(v) : null; } catch { return null; }
}
/** A block pasted from Excel: tab-separated rows; text cells come through as null. */
export function parseBlock(text: string): (number | null)[][] {
  return text.replace(/\r/g, "").replace(/\n$/, "").split("\n").map((line) => line.split("\t").map((c) => parseEntry(c, 0)));
}

const ROW_H = 30, NUM_W = 40;

export function SheetGrid({ cols, rows, onEdit, onSelect, onStats, onUndo, label, empty, lockedHint }: {
  cols: SCol[]; rows: SRow[]; label: string; empty?: ReactNode; lockedHint?: string;
  onEdit: (edits: SEdit[], what: "cell" | "paste" | "fill" | "clear") => void;
  onSelect?: (cell: { row: number; col: number } | null) => void; onStats?: (s: SStats | null) => void; onUndo?: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [sel, setSel] = useState<{ r0: number; c0: number; r1: number; c1: number } | null>(null);
  const [editing, setEditing] = useState<{ r: number; c: number; text: string } | null>(null);
  const [bad, setBad] = useState<string | null>(null);
  const drag = useRef(false);
  const norm = sel ? { r0: Math.min(sel.r0, sel.r1), r1: Math.max(sel.r0, sel.r1), c0: Math.min(sel.c0, sel.c1), c1: Math.max(sel.c0, sel.c1) } : null;
  const inSel = (r: number, c: number) => !!norm && r >= norm.r0 && r <= norm.r1 && c >= norm.c0 && c <= norm.c1;
  const cell = (r: number, c: number) => rows[r]?.cells[c];

  useEffect(() => { const up = () => { drag.current = false; }; window.addEventListener("mouseup", up); return () => window.removeEventListener("mouseup", up); }, []);
  useEffect(() => { onSelect?.(sel ? { row: sel.r1, col: sel.c1 } : null); }, [sel?.r1, sel?.c1]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!onStats) return;
    if (!norm) return onStats(null);
    let count = 0, nums = 0, sum = 0;
    for (let r = norm.r0; r <= norm.r1; r++) for (let c = norm.c0; c <= norm.c1; c++) { const v = cell(r, c)?.v; if (v === null || v === undefined || v === "") continue; count++; if (typeof v === "number") { nums++; sum += v; } }
    onStats({ count, nums, sum });
  }, [sel, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (r: number, c: number) => {
    const el = box.current; if (!el) return;
    const y = (r + 1) * ROW_H;
    if (y - ROW_H < el.scrollTop + ROW_H) el.scrollTop = Math.max(0, y - 2 * ROW_H);
    else if (y > el.scrollTop + el.clientHeight) el.scrollTop = y - el.clientHeight + 4;
    const x = NUM_W + cols.slice(0, c).reduce((t, k) => t + k.width, 0), pin = NUM_W + cols.filter((k) => k.pinned).reduce((t, k) => t + k.width, 0);
    if (!cols[c]?.pinned) { if (x < el.scrollLeft + pin) el.scrollLeft = Math.max(0, x - pin); else if (x + cols[c].width > el.scrollLeft + el.clientWidth) el.scrollLeft = x + cols[c].width - el.clientWidth; }
  };
  const moveTo = (r: number, c: number, extend = false) => {
    r = Math.max(0, Math.min(rows.length - 1, r)); c = Math.max(0, Math.min(cols.length - 1, c));
    setSel(extend && sel ? { ...sel, r1: r, c1: c } : { r0: r, c0: c, r1: r, c1: c });
    scrollTo(r, c);
  };
  const commit = (move: [number, number] | null) => {
    if (!editing) return;
    const cur = cell(editing.r, editing.c);
    const v = parseEntry(editing.text, typeof cur?.v === "number" ? cur.v : 0);
    if (v === null) { setBad(`"${editing.text}" isn't a number. Try 12500, 12.5k, +5% or =1000*12.`); return; }
    setBad(null);
    if (cur?.edit && v !== cur.v) onEdit([{ row: editing.r, col: editing.c, value: v }], "cell");
    setEditing(null);
    box.current?.focus({ preventScroll: true });
    if (move) moveTo(editing.r + move[0], editing.c + move[1]);
  };
  const editableIn = (fn: (r: number, c: number) => number | null) => {
    if (!norm) return [];
    const out: SEdit[] = [];
    for (let r = norm.r0; r <= norm.r1; r++) for (let c = norm.c0; c <= norm.c1; c++) { if (!cell(r, c)?.edit) continue; const v = fn(r, c); if (v !== null && v !== cell(r, c)?.v) out.push({ row: r, col: c, value: v }); }
    return out;
  };
  const copy = () => {
    if (!norm) return;
    const lines: string[] = [];
    for (let r = norm.r0; r <= norm.r1; r++) { const o: string[] = []; for (let c = norm.c0; c <= norm.c1; c++) { const v = cell(r, c)?.v; o.push(v === null || v === undefined ? "" : String(v)); } lines.push(o.join("\t")); }
    navigator.clipboard?.writeText(lines.join("\n")).catch(() => {});
  };
  const paste = (text: string) => {
    if (!sel) return;
    const block = parseBlock(text);
    const r0 = Math.min(sel.r0, sel.r1), c0 = Math.min(sel.c0, sel.c1);
    // A single value pasted over a selection fills it, like Excel.
    const single = block.length === 1 && block[0].length === 1 && norm && (norm.r1 > norm.r0 || norm.c1 > norm.c0);
    const edits: SEdit[] = single ? editableIn(() => block[0][0]) : block.flatMap((line, i) => line.map((v, j) => ({ row: r0 + i, col: c0 + j, value: v }))).filter((e): e is SEdit => e.value !== null && !!cell(e.row, e.col)?.edit);
    const skipped = single ? 0 : block.flat().filter((v) => v !== null).length - edits.length;
    if (edits.length) onEdit(edits, "paste");
    setBad(skipped > 0 ? `${skipped} pasted ${skipped === 1 ? "value landed" : "values landed"} on totals or driven lines and ${skipped === 1 ? "was" : "were"} skipped.` : null);
    if (!single) setSel({ r0, c0, r1: Math.min(rows.length - 1, r0 + block.length - 1), c1: Math.min(cols.length - 1, c0 + Math.max(...block.map((l) => l.length)) - 1) });
  };
  const key = (e: React.KeyboardEvent) => {
    if (editing) return;
    const mod = e.ctrlKey || e.metaKey, k = e.key.toLowerCase();
    if (mod && k === "c") { e.preventDefault(); copy(); return; }
    if (mod && k === "z") { e.preventDefault(); onUndo?.(); return; }
    if (mod && k === "a") { e.preventDefault(); if (rows.length) setSel({ r0: 0, c0: 0, r1: rows.length - 1, c1: cols.length - 1 }); return; }
    if (mod && k === "r" && norm) { e.preventDefault(); const ed = editableIn((r) => { const v = cell(r, norm.c0)?.v; return typeof v === "number" ? v : null; }); if (ed.length) onEdit(ed, "fill"); return; }
    if (mod && k === "d" && norm) { e.preventDefault(); const ed = editableIn((_, c) => { const v = cell(norm.r0, c)?.v; return typeof v === "number" ? v : null; }); if (ed.length) onEdit(ed, "fill"); return; }
    if (!sel) return;
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); const ed = editableIn(() => 0); if (ed.length) onEdit(ed, "clear"); return; }
    if (e.key === "F2") { e.preventDefault(); startEdit(sel.r1, sel.c1); return; }
    const mv: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1], Tab: [0, e.shiftKey ? -1 : 1], Enter: [e.shiftKey ? -1 : 1, 0], PageDown: [15, 0], PageUp: [-15, 0], Home: [0, -999], End: [0, 999] };
    const d = mv[e.key];
    if (d) { e.preventDefault(); const jump = mod && e.key.startsWith("Arrow"); moveTo(jump && d[0] ? (d[0] > 0 ? rows.length - 1 : 0) : sel.r1 + d[0], jump && d[1] ? (d[1] > 0 ? cols.length - 1 : 0) : sel.c1 + d[1], e.shiftKey && e.key.startsWith("Arrow")); return; }
    if (!mod && e.key.length === 1 && /[0-9.+\-=(kKmM$]/.test(e.key) && cell(sel.r1, sel.c1)?.edit) { e.preventDefault(); setSel({ r0: sel.r1, c0: sel.c1, r1: sel.r1, c1: sel.c1 }); setEditing({ r: sel.r1, c: sel.c1, text: e.key }); }
  };
  const startEdit = (r: number, c: number) => { const cl = cell(r, c); if (!cl?.edit) { setBad(cl && cols[c]?.num && rows[r]?.kind === "line" ? lockedHint ?? "This cell is worked out from a driver or the version can't be changed." : null); return; } setSel({ r0: r, c0: c, r1: r, c1: c }); setEditing({ r, c, text: typeof cl.v === "number" ? String(cl.v) : "" }); };

  const lefts: (number | null)[] = []; { let x = NUM_W; for (const c of cols) { lefts.push(c.pinned ? x : null); if (c.pinned) x += c.width; } }
  const width = NUM_W + cols.reduce((t, c) => t + c.width, 0);
  const style = (i: number) => ({ width: cols[i].width, minWidth: cols[i].width, maxWidth: cols[i].width, ...(lefts[i] !== null ? { position: "sticky" as const, left: lefts[i]!, zIndex: 1 } : {}) });

  return (
    <div className="sg-wrap">
      <div className="grid-box sg" ref={box} tabIndex={0} role="grid" aria-label={label} onKeyDown={key} onPaste={(e) => { if (editing) return; e.preventDefault(); paste(e.clipboardData.getData("text/plain")); }}>
        <table className="g" style={{ width }}>
          <thead><tr>
            <th className="g-num g-corner" onClick={() => rows.length && setSel({ r0: 0, c0: 0, r1: rows.length - 1, c1: cols.length - 1 })} />
            {cols.map((c, i) => <th key={c.key} style={style(i)} className={`${c.num ? "r" : ""}${c.pinned ? " pin" : ""}`} title={c.sub}><span className="g-h sg-h" onClick={() => rows.length && setSel({ r0: 0, c0: i, r1: rows.length - 1, c1: i })}>{c.label}</span></th>)}
            <th className="g-fill" />
          </tr></thead>
          <tbody>{rows.map((row, r) => (
            <tr key={row.key} className={`sg-${row.kind}${row.cls ? ` ${row.cls}` : ""}`} style={{ height: ROW_H }}>
              <th className="g-num" scope="row">{r + 1}</th>
              {row.cells.map((cl, c) => {
                const isEdit = editing && editing.r === r && editing.c === c;
                return (
                  <td key={c} style={style(c)} className={`${cols[c].num ? "r" : ""}${cols[c].pinned ? " pin" : ""}${inSel(r, c) ? " sel" : ""}${sel && sel.r1 === r && sel.c1 === c ? " cur" : ""}${cl.edit ? " ed" : ""}${cl.tone ? ` t-${cl.tone}` : ""}${cl.cls ? ` ${cl.cls}` : ""}`} title={cl.title}
                    onMouseDown={(e) => { if (e.button !== 0 || isEdit) return; if (editing) commit(null); drag.current = true; box.current?.focus({ preventScroll: true }); setSel(e.shiftKey && sel ? { ...sel, r1: r, c1: c } : { r0: r, c0: c, r1: r, c1: c }); }}
                    onMouseEnter={() => { if (drag.current && sel) setSel({ ...sel, r1: r, c1: c }); }}
                    onDoubleClick={() => startEdit(r, c)}>
                    {isEdit ? <input ref={input} autoFocus onFocus={(e) => { const n = e.target.value.length; e.target.setSelectionRange(n, n); }} className="sg-in" value={editing.text} aria-label="Cell value" onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit([e.shiftKey ? -1 : 1, 0]); } else if (e.key === "Tab") { e.preventDefault(); commit([0, e.shiftKey ? -1 : 1]); } else if (e.key === "Escape") { e.preventDefault(); setEditing(null); setBad(null); box.current?.focus({ preventScroll: true }); } }}
                      onBlur={() => commit(null)} /> : cl.text}
                  </td>
                );
              })}
              <td className="g-fill" />
            </tr>
          ))}</tbody>
        </table>
        {!rows.length && empty ? <div className="g-empty">{empty}</div> : null}
      </div>
      {bad && <div className="sg-bad" role="alert">{bad}</div>}
    </div>
  );
}
